import { Component, computed, inject, input, linkedSignal, output, viewChild } from '@angular/core';
import { IonButton, IonCardContent, IonChip, IonIcon, IonLabel, ModalController } from '@ionic/angular';
import { TranslatePipe } from '@ngx-translate/core';
import { ToastService } from '../../../common/services/toast-service/toast-service.service';
import { IonicColor } from '../../../common/entities/toast/ionic-color';
import { CurafidaCardComponent } from '../../../common/components/curafida-card/curafida-card.component';
import { LoggingService } from '../../../logging/logging.service';
import { User } from '../../entities/user';
import { UserRoles } from '../../entities/user-roles';
import { UserTagsService } from '../../services/user-tags/user-tags.service';
import { UserTag, UserTagAssignmentDto } from '../../entities/user-tag/user-tag.entity';
import { ICardActionButton } from '../../../common/entities/card-action-button.interface';
import { FilterModalComponent } from '../../../common/components/filter-modal/filter-modal.component';
import { UserTagCategory } from '../../entities/user-tag/user-tag-category.entity';
import { FilterTag } from '../../../common/entities/filter-tag';

@Component({
    selector: 'lib-user-tag-view',
    templateUrl: './user-tag-view.component.html',
    styleUrls: ['./user-tag-view.component.scss'],
    imports: [IonCardContent, CurafidaCardComponent, IonLabel, IonChip, IonButton, IonIcon, TranslatePipe],
})
export class UserTagViewComponent {
    private readonly toastService = inject(ToastService);
    private readonly userTagsService = inject(UserTagsService);
    private readonly modalController = inject(ModalController);
    private readonly log = inject(LoggingService).getLogger('UserTagViewComponent');

    readonly user = input<User>();
    readonly mainUserRole = input<UserRoles>();
    readonly loggedInUser = input<User>();
    readonly isMobile = input(false);
    readonly isNewElement = input(false);
    readonly isButtonHidden = input(false);
    readonly updateMainUserComponent = output();
    readonly title = 'TAG.PLURAL';
    /** The tags as last saved; reset whenever the parent passes another user. */
    readonly tags = linkedSignal<UserTag[]>(() => this.user()?.tags ?? []);
    /** Hiding the buttons always leaves edit mode. */
    readonly isEditEnabled = linkedSignal({ source: this.isButtonHidden, computation: () => false });
    readonly tagCategories = computed<UserTagCategory[]>(() => {
        const categories = new Map<string, UserTagCategory>();
        for (const tag of this.tags()) {
            if (!tag.category) {
                continue;
            }
            const category = categories.get(tag.category.uuid) ?? { ...tag.category, tags: [] };
            category.tags.push(tag);
            categories.set(tag.category.uuid, category);
        }
        return [...categories.values()];
    });
    readonly actionButtons = computed<ICardActionButton[]>(() =>
        this.isButtonHidden()
            ? []
            : [
                  {
                      icon: 'add',
                      id: 'add-care',
                      isDisabled: false,
                      isHidden: false,
                      isIconButton: this.isMobile(),
                      title: 'TAG.ADD_NEW',
                  },
              ],
    );
    private readonly curafidaCardComponent = viewChild.required<CurafidaCardComponent>('curafidaCardComponent');
    private tagModalOpened = false;

    async addNewTags(): Promise<void> {
        if (this.tagModalOpened) {
            return;
        }
        this.tagModalOpened = true;
        let selectedTags: FilterTag[] | undefined;
        try {
            const response = await this.userTagsService.getUserTagCategories({
                tenantOfUser: this.loggedInUser()?.groups?.[0],
                userRole: this.mainUserRole(),
            });
            // The modal hands back these tag objects, which need their category to be grouped afterwards.
            response.items.forEach((category) => category.tags.forEach((tag) => (tag.category = category)));

            const modal = await this.modalController.create({
                component: FilterModalComponent,
                cssClass: 'modal-tag-list-css',
                componentProps: {
                    title: `${this.user().firstname} ${this.user().lastname}`,
                    // The modal mutates its selection, so it must not get the displayed array.
                    selectedTags: [...this.tags()],
                    tagCategories: response.items.map((category) => ({
                        label: category.label,
                        uuid: category.label,
                        tags: category.tags,
                    })),
                },
            });
            await modal.present();
            selectedTags = (await modal.onDidDismiss<FilterTag[]>()).data;
        } catch (err) {
            this.log.error('Error in addNewTags', err);
        } finally {
            this.tagModalOpened = false;
        }

        if (selectedTags && (await this.saveTags(selectedTags as UserTag[]))) {
            this.curafidaCardComponent().isCardCollapsed.set(false);
        }
    }

    async removeTag(tag: UserTag): Promise<void> {
        if (!this.isEditEnabled()) {
            return;
        }
        await this.saveTags(this.tags().filter((userTag) => userTag.uuid !== tag.uuid));
    }

    toggleEdit(): void {
        this.isEditEnabled.set(true);
    }

    cancelEdit(): void {
        this.isEditEnabled.set(false);
    }

    /** Shows the new tags right away and restores the previous ones if the request fails. */
    private async saveTags(tags: UserTag[]): Promise<boolean> {
        const user = this.user();
        const previousTags = this.tags();
        this.tags.set(tags);
        try {
            const dto = new UserTagAssignmentDto(tags.map((tag) => tag.uuid));
            if (user.roles?.includes(UserRoles.PATIENT)) {
                await this.userTagsService.updateUserTagFromPatient(user.username, dto);
            } else {
                await this.userTagsService.updateUserTagFromUser(user.username, dto);
            }
            // The parent shares this user object with the rest of the page, so keep its tags in sync once saved.
            user.tags = tags;
            this.updateMainUserComponent.emit();
            this.toastService.showToast(ToastService.changeSavedMessage, IonicColor.success);
            return true;
        } catch (err) {
            this.tags.set(previousTags);
            this.log.error('Error in saveTags', err);
            return false;
        }
    }
}
